import {
  Button,
  ErrorText,
  Form,
  FormControl,
  FormItem,
  PhoneSelectInput,
} from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { Country } from '@/shared/types';
import type { PhoneFormValues } from '@/widgets/phone-verification';

interface Props {
  form: ReturnType<typeof useForm<PhoneFormValues>>;
  isCountriesLoading?: boolean;
  isSendTwoFactorIdentityLoading?: boolean;
  onSubmit: (data: PhoneFormValues) => void;
  sendTwoFactorIdentityError?: Error | null;
  validCountries: Country[];
}

export const PhoneVerificationForm = ({
  form,
  isCountriesLoading,
  isSendTwoFactorIdentityLoading,
  onSubmit,
  sendTwoFactorIdentityError,
  validCountries,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-[3.2rem]">
        <FormItem>
          <FormControl>
            <PhoneSelectInput
              countries={validCountries}
              countryCodeValue={form.watch('countryCode')}
              phoneNumberValue={form.watch('phoneNumber')}
              onCountryCodeChange={phoneCode => {
                form.setValue('countryCode', phoneCode, {
                  shouldValidate: true,
                });
              }}
              onPhoneNumberChange={number => {
                form.setValue('phoneNumber', number, {
                  shouldValidate: true,
                });
              }}
              placeholder={t('authPages.phoneForm.phonePlaceholder')}
              disabled={isCountriesLoading || isSendTwoFactorIdentityLoading}
              errorCountryCode={form.formState.errors.countryCode}
              errorPhoneNumber={form.formState.errors.phoneNumber}
            />
          </FormControl>
        </FormItem>

        {sendTwoFactorIdentityError && (
          <div className="text-center">
            <ErrorText size="small">
              {sendTwoFactorIdentityError?.message || t('common.unknownError')}
            </ErrorText>
          </div>
        )}

        <Button
          type="submit"
          size="lg"
          disabled={isCountriesLoading || isSendTwoFactorIdentityLoading}
          loading={isSendTwoFactorIdentityLoading}
          className="h-[6rem]">
          <span className="headline4">{t('common.continue')}</span>
        </Button>
      </form>
    </Form>
  );
};
